import React, { useState, useEffect, useRef } from 'react';
import { useSnackbar } from 'notistack';
import {
  Box,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  Divider,
  Button,
  Typography,
  CircularProgress,
  TextField,
} from '@mui/material';
import { getYears } from '../../utils/constCodeUtils';
import * as xlsx from 'xlsx';
import {
  makeAccountSalesData,
  makeAccountSalesHeader,
} from '../../utils/eis-statics-data';

import * as salesCostsApi from '../../lib/api/salesCosts';

import AccountSalesMangageStaticTable from '../../components/account-sales-manage-statics/AccountSalesMangageStaticTable';
import { useDispatch, useSelector } from 'react-redux';
import { getCompanies } from '../../modules/auth';

const AccountSalesManageStaticsContainer = () => {
  const tableRef = useRef();

  const dispatch = useDispatch();
  const { companies } = useSelector(({ auth }) => ({
    companies: auth.companies,
  }));
  const [companyCd, setCompanyCd] = useState('ALL');
  const [years, setYears] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [headerRows, setHeaderRows] = useState(null);

  useEffect(() => {
    const arrYears = getYears(3, 1);
    setYears(arrYears);
  }, []);

  const handleChangeYear = (event) => {
    setSelectedYear(event.target.value);
  };

  const handleCompany = (event) => {
    setCompanyCd(event.target.value);
  };

  const handleDownExcel = () => {
    const el = tableRef;
    const name = '계정별 판매관리비 통계';
    htmlToExcel(el.current, name);
  };

  const htmlToExcel = (tableEl, name) => {
    const wb = xlsx.utils.table_to_book(tableEl, {
      sheet: 'sheet1',
      raw: true,
    });
    xlsx.writeFile(wb, name + '.xlsx');
  };

  useEffect(() => {
    if (!companies || companies.length === 0) {
      dispatch(getCompanies());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, companies]);

  useEffect(() => {
    if (selectedYear && companies) {
      const headers = makeAccountSalesHeader();
      setHeaderRows(headers);
      //const tempData = makeAccountSalesData(companies);
      //console.log(JSON.stringify(tempData));

      // getSalesCostsStaticTotal는 axios Promise를 돌려준다 - await/then 없이 response.data를
      // 바로 읽으면 Promise 객체가 들어가 테이블이 undefined.length에서 죽는다(2026-10-20 발견).
      // 응답 모양도 배열이 아니라 { list: [...] }(계정과목별 평평한 목록)라 response.data를 그대로
      // 넣으면 테이블이 data.map에서 또 죽는다. 게다가 이 페이지(계정별*회사별*) 행 컴포넌트는
      // "회사 → accounts[]" 로 묶인 모양을 기대하는데(여러 회사를 다루던 원본 ksm 구조), bito는
      // 회사가 1개뿐이고 이 API도 회사별로 나눠주지 않는다 - 평평한 목록을 회사 1개짜리 그룹으로
      // 감싸서 형제 컨테이너(sales_mng_cost_stats)와 같은 원본 데이터를 그대로 보여준다.
      setLoading(true);
      salesCostsApi
        .getSalesCostsStaticTotal(selectedYear)
        .then((response) => {
          const accounts = response.data?.list || [];
          const companyNm =
            companies?.find((c) => c.companyCd === companyCd)?.companyNm ||
            companies?.[0]?.companyNm ||
            '전체';
          setData(
            accounts.length
              ? [{ companyNm, rowSpan: accounts.length, accounts }]
              : [],
          );
        })
        .catch(() => {
          enqueueSnackbar('계정별 판매관리비 통계를 불러오지 못했습니다.', {
            variant: 'error',
          });
          setData([]);
        })
        .finally(() => {
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, selectedYear]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', my: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          <Box sx={{ mx: 1, minWidth: 120 }}>
            <TextField
              variant="standard"
              fullWidth
              select
              label="회사"
              name="company"
              value={companyCd}
              onChange={handleCompany}
            >
              <MenuItem value="ALL">전체</MenuItem>
              {companies &&
                companies.map((option) => (
                  <MenuItem key={option.companyCd} value={option.companyCd}>
                    {option.companyNm}
                  </MenuItem>
                ))}
            </TextField>
          </Box>
          <Box sx={{ mr: 1, minWidth: 120 }}>
            <FormControl variant="standard" fullWidth>
              <InputLabel id="selected-year-label">기준년도</InputLabel>
              <Select
                labelId="selected-year-label"
                id="selected-year"
                value={selectedYear}
                label="년도"
                onChange={handleChangeYear}
              >
                {years &&
                  years.map((item, index) => (
                    <MenuItem key={index} value={item}>
                      {item}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          <Box>
            <Button
              variant="contained"
              color="success"
              onClick={handleDownExcel}
            >
              엑셀 다운로드
            </Button>
          </Box>
        </Box>
      </Box>
      <Divider sx={{ my: 1 }} />

      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        {loading ? (
          <Box sx={{ display: 'flex' }}>
            <CircularProgress />
          </Box>
        ) : Array.isArray(data) && data.length === 0 ? (
          <Box>
            <Typography variant="subtitle2">데이터가 없습니다.</Typography>
          </Box>
        ) : (
          <AccountSalesMangageStaticTable
            data={data}
            loading={loading}
            headerRows={headerRows}
            tableRef={tableRef}
          />
        )}
      </Box>
    </Box>
  );
};
export default AccountSalesManageStaticsContainer;
